import { TFunction } from "i18next";

import {
	CreditQuoteAction,
	CreditQuoteResponse,
	CreditQuoteSegmentKind,
} from "@signalist/shared-types";

import { CreditSegmentView, formatCredits } from "./creditBreakdown";

/** The translation namespace of each quoted action under `creditConfirm`. */
export const QUOTE_ACTION_COPY: Record<CreditQuoteAction, string> = {
	EMAIL: "email",
	PHONE: "phone",
	COMPANY_CONTACT: "companyContact",
	RESCORE: "rescore",
};

/** The parts each action runs on and charges for only when it finds something. */
const RESULT_DEPENDENT_KINDS = new Set<CreditQuoteSegmentKind>(["LOOKUP", "COMPANY_LOOKUP"]);

/** The parts each action runs on; every other part is shown as skipped. */
const INCLUDED_KINDS = new Set<CreditQuoteSegmentKind>(["LOOKUP", "COMPANY_LOOKUP", "NOT_SCORED"]);

/**
 * The server's quote as the modal's rows (PREC-4263). Lookups are charged only for what they find
 * (a phone lookup is charged upfront and refunded on a miss), so their cost is a maximum. Already
 * scored leads are rescored, and charged, only when the user asks for it: otherwise the retrigger
 * skips them, and so does the quote.
 */
export function quoteSegmentViews(
	quote: CreditQuoteResponse,
	t: TFunction,
	{ rescore = false }: { rescore?: boolean } = {}
): CreditSegmentView[] {
	const copy = QUOTE_ACTION_COPY[quote.action];

	return quote.segments.map((segment) => {
		const included = INCLUDED_KINDS.has(segment.kind) || (segment.kind === "SCORED" && rescore);
		const key = segment.kind === "SCORED" && !rescore ? "SCORED_SKIPPED" : segment.kind;
		// A lead whose job offer or Maps business is searched as well holds more than one charge.
		const holdsMore = (segment.lookups ?? segment.count) > segment.count;

		return {
			id: segment.kind,
			label: t(`creditConfirm.${copy}.${key}${holdsMore ? "_ENTITIES" : ""}`, {
				count: segment.count,
				lookups: segment.lookups,
				unit: formatCredits(segment.unitCredits),
			}),
			count: segment.count,
			chargedUnits: segment.lookups,
			unitCredits: segment.unitCredits,
			included,
			resultDependent: RESULT_DEPENDENT_KINDS.has(segment.kind),
		};
	});
}
